import React from 'react';
import LogoCard from '@components/logo-card/logo-card';
import { Carousel } from '@components/carousel/carousel';
import styled from '@emotion/styled/macro';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { H2Atom } from '@atoms/text-atoms/text-atoms';
import { LogoBlockProps } from './logo-block.types';
import { sectionTitleBorder } from '../../mixins';
import { defineResponsiveGridColumns, generateKey } from '@utils/utils';
import {
  CarouselContainer,
  StyledCarouselItem,
} from '@components/carousel/carousel.styles';

const LogoBlock: React.FC<LogoBlockProps> = ({
  title = '',
  anchorId,
  logos = [],
  className,
  colour = vars.coreDarkGrey,
  translate = true,
  displayAsCarousel = false,
}) => {
  if (!logos || logos.length < 1) return null;

  const shouldRenderCarousel = displayAsCarousel;

  return (
    <div id={anchorId} data-testid="logo-block" className={className}>
      {title && (
        <H2Atom className="title">{Translate(title, translate)}</H2Atom>
      )}
      {shouldRenderCarousel ? (
        <Carousel
          visibleTilesCount={6}
          items={logos.map((logo, index) => (
            <LogoCard
              key={generateKey(logo.name, index)}
              name={logo.name}
              type={logo.type}
              url={logo.url}
              gatsbyImageData={logo.gatsbyImageData}
              logoSrc={logo.logoSrc}
              logoAlt={logo.logoAlt}
              linkText={logo.linkText}
            />
          ))}
          arrowTopSpacing="35%"
        />
      ) : (
        <div className="component-grid-structure">
          <div className="logos-container">
            {logos.map((logo, index) => {
              return (
                <LogoCard
                  key={generateKey(logo.name, index)}
                  name={logo.name}
                  type={logo.type}
                  url={logo.url}
                  gatsbyImageData={logo.gatsbyImageData}
                  logoSrc={logo.logoSrc}
                  logoAlt={logo.logoAlt}
                  linkText={logo.linkText}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const StyledLogoBlock = styled(LogoBlock)`
  margin: ${vars.spacingXxl} auto;
  .title {
    ${({ colour }) => sectionTitleBorder(colour)}
    &::first-letter {
      text-transform: uppercase;
    }
    margin-bottom: ${vars.spacingS};
    @media screen and (min-width: ${vars.breakPointTenColumn}) {
      margin-bottom: ${vars.spacingM};
    }
    @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
      max-width: 70%;
    }
  }
  .component-grid-structure {
    column-gap: ${vars.mobileColumnPercentageGap};
    grid-template-columns: ${defineResponsiveGridColumns(4)};
    @media screen and (min-width: ${vars.breakPointTenColumn}) {
      column-gap: ${vars.standardColumnPercentageGap};
      grid-template-columns: ${defineResponsiveGridColumns(12)};
    }
    .title {
      grid-column: col1-start / col3-end;
      ${({ colour }) => sectionTitleBorder(colour)}
      &::first-letter {
        text-transform: uppercase;
      }
      margin-bottom: ${vars.spacingS};
      @media screen and (min-width: ${vars.breakPointTenColumn}) {
        grid-column: col1-start / col5-end;
        margin-bottom: ${vars.spacingM};
      }
    }
  }

  ${CarouselContainer} {
    ${StyledCarouselItem} {
      @media screen and (max-width: ${vars.breakPointTablet}) {
        max-width: 195px;
      }
    }
  }

  .logos-container {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;

    ${LogoCard} {
      width: calc((100% - (${vars.spacingM} * 5)) / 6); /* 6 per row */
      margin-right: ${vars.spacingM};
      margin-bottom: ${vars.spacingM};

      &:nth-of-type(6n) {
        margin-right: 0;
      }

      &:nth-of-type(odd) {
        background-color: ${vars.greyHavens};
      }

      @media screen and (max-width: 1297px) {
        width: calc((100% - (${vars.spacingM} * 4)) / 5);

        &:nth-of-type(6n) {
          margin-right: ${vars.spacingM};
        }

        &:nth-of-type(5n) {
          margin-right: 0;
        }
      }

      @media screen and (max-width: 1086px) {
        width: calc((100% - (${vars.spacingM} * 3)) / 4);

        &:nth-of-type(5n) {
          margin-right: ${vars.spacingM};
        }

        &:nth-of-type(4n) {
          margin-right: 0;
        }
      }

      @media screen and (max-width: ${vars.breakPointTablet}) {
        width: calc((100% - (${vars.spacingM} * 2)) / 3);

        &:nth-of-type(4n) {
          margin-right: ${vars.spacingM};
        }

        &:nth-of-type(3n) {
          margin-right: 0;
        }
      }

      @media screen and (max-width: 580px) {
        width: calc((100% - ${vars.spacingM}) / 2);

        &:nth-of-type(3n) {
          margin-right: ${vars.spacingM};
        }

        &:nth-of-type(2n) {
          margin-right: 0;
        }
      }
    }
  }
`;

export default StyledLogoBlock;
